import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useClientAuth } from '../hooks/useClientAuth';
import { NotificationBell } from '../../components/NotificationBell';

// Query to get user's cases to determine navigation
const LIST_MY_CASES = gql`
  query ListMyCases {
    listMyCases {
      id
      status
    }
  }
`;

// Query to get user's inquiry (singular - prospects have one inquiry)
const GET_MY_INQUIRY = gql`
  query GetMyInquiry {
    getMyInquiry {
      id
      status
    }
  }
`;

interface ClientLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: JSX.Element;
}

/**
 * ClientLayout - Main layout wrapper for client portal
 * 
 * Provides:
 * - Responsive navigation (sidebar on desktop/tablet 768px+, bottom tabs on mobile <768px)
 * - Header with logo and user info
 * - Main content area
 * - Consistent styling matching mobile app theme (AA Law colors)
 * - Touch-friendly button sizes and spacing for mobile
 * - Safe area insets for notched devices
 * 
 * Requirements: 10.1, 10.2, 10.3, 10.7, 10.8, 10.9
 */
export function ClientLayout({ children }: ClientLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useClientAuth();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Fetch user's cases to determine if they have active cases
  const { data: casesData } = useQuery(LIST_MY_CASES, {
    fetchPolicy: 'cache-first',
    skip: !user,
  });

  // Fetch user's inquiry (prospects have one inquiry)
  const { data: inquiryData } = useQuery(GET_MY_INQUIRY, {
    fetchPolicy: 'cache-first',
    skip: !user,
  });

  // Determine if user has active cases (cases with status other than INQUIRY)
  const { hasActiveCases, hasPendingInquiries } = useMemo(() => {
    const cases = (casesData as any)?.listMyCases || [];
    const inquiry = (inquiryData as any)?.getMyInquiry;
    
    // Active cases are those with status other than INQUIRY (placeholder status)
    const activeCases = cases.filter((c: any) => c.status !== 'INQUIRY');
    
    return {
      hasActiveCases: activeCases.length > 0,
      hasPendingInquiries: !!inquiry,
    };
  }, [casesData, inquiryData]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Build navigation items dynamically based on user's cases/inquiries
  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      {
        path: '/client/news',
        label: 'News',
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        ),
      },
    ];

    // Add case tab if user has active cases
    if (hasActiveCases) {
      items.push({
        path: '/client/case',
        label: 'My Case',
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        ),
      });
    }

    // Add inquiry tab if user has pending inquiries (or no active cases)
    if (hasPendingInquiries || !hasActiveCases) {
      items.push({
        path: '/client/inquiry',
        label: 'My Inquiry',
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      });
    }

    // Add remaining nav items
    items.push(
      {
        path: '/client/documents',
        label: 'Documents',
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
      {
        path: '/client/chat',
        label: 'Chat',
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        ),
      },
      {
        path: '/client/tasks',
        label: 'Tasks',
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
      },
      {
        path: '/client/calendar',
        label: 'Calendar',
        icon: (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      }
    );

    return items;
  }, [hasActiveCases, hasPendingInquiries]);

  // More menu items (Profile, About Us)
  const moreMenuItems: NavItem[] = [
    {
      path: '/client/profile',
      label: 'Profile',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      path: '/client/about',
      label: 'About Us',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Loading state
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
          <div className="h-4 w-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
      {/* Header - Always visible */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => navigate('/client/news')}
            >
              <img 
                src="/icon.png" 
                alt="AA Law Logo" 
                className="w-9 h-9 md:w-10 md:h-10 rounded-lg object-contain hover:scale-105 transition-transform"
              />
              <span className="ml-3 text-lg font-semibold text-aalaw-charcoal hidden sm:block">
                AA Law
              </span>
            </div>

            {/* Right side: Notification Bell & Profile Menu */}
            <div className="flex items-center gap-2">
              {/* Notification Bell */}
              <NotificationBell />

              {/* User Info & Profile Menu */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
                  aria-label="User menu"
                >
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-semibold text-gray-900">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-gray-600 capitalize">
                      {user.role.toLowerCase()}
                    </p>
                  </div>
                  <div className="w-9 h-9 bg-gradient-to-br from-aalaw-red to-aalaw-red-dark rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {user.firstName[0]}{user.lastName[0]}
                    </span>
                  </div>
                  <svg className="w-4 h-4 text-gray-500 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Profile Dropdown */}
                {profileMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    {/* User info header - mobile only */}
                    <div className="px-4 py-3 border-b border-gray-100 sm:hidden">
                      <p className="text-sm font-semibold text-gray-900">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs text-gray-600 capitalize">
                        {user.role.toLowerCase()}
                      </p>
                    </div>

                    {/* Profile and About Us - always shown */}
                    {moreMenuItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => {
                        handleNavigation(item.path);
                        setProfileMenuOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 transition-colors ${
                        isActive(item.path)
                          ? 'bg-red-50 text-aalaw-red font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </button>
                  ))}
                  
                  <div className="border-t border-gray-100 my-2"></div>

                  {/* Submit New Inquiry */}
                  <button
                    onClick={() => {
                      navigate('/inquiry');
                      setProfileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-aalaw-red hover:bg-red-50 flex items-center gap-3 transition-colors font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Submit New Inquiry</span>
                  </button>
                  
                  <div className="border-t border-gray-100 my-2"></div>

                  {/* Sign Out */}
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout Container */}
      <div className="flex flex-1 w-full max-w-full overflow-hidden">
        {/* Sidebar Navigation - Desktop/Tablet (768px+) */}
        <aside className="hidden md:flex md:flex-col md:w-64 md:flex-shrink-0 bg-white border-r border-gray-200 sticky top-16 h-[calc(100vh-4rem)]">
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-aalaw-red text-white shadow-sm'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
          
          {/* Submit New Inquiry Button */}
          <div className="px-4 py-4 border-t border-gray-200">
            <button
              onClick={() => navigate('/inquiry')}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg text-sm font-medium bg-aalaw-red text-white hover:bg-red-700 transition-all duration-200 shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Submit New Inquiry</span>
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 pb-20 md:pb-0 min-w-0 overflow-x-hidden">
          <div className="p-0 sm:p-4 lg:p-6 w-full">
            {children}
          </div>
        </main>
      </div>

      {/* Bottom Tab Bar - Mobile (<768px) - Touch-friendly with min 44px tap targets */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-safe">
        <div className="flex items-center justify-around h-16 px-0 safe-area-inset-bottom">
          {/* All 6 nav items */}
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className={`flex flex-col items-center justify-center flex-1 min-h-[48px] py-1 px-0.5 transition-colors active:scale-95 active:bg-gray-100 rounded-lg ${
                isActive(item.path)
                  ? 'text-aalaw-red'
                  : 'text-gray-500'
              }`}
              aria-label={item.label}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <div className="w-5 h-5">{item.icon}</div>
              <span className="text-[9px] mt-0.5 font-medium leading-tight">
                {item.label === 'Documents' ? 'Docs' : item.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
